import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {
  adjustToTimeZone,
  Dispatch,
  DispatchRequest,
  dispatchRequestStatus,
  useAcceptDispatchRequestMutation,
  useAddDispatchToDispatchRequestMutation,
  useReassignDispatchRequestMutation,
  useRejectDispatchRequestMutation,
  useUpdateDispatchRequestMutation
} from "@ots-frontend/ots-data";
import {useAuth} from "../providers/AuthProvider/AuthProvider";

export type UseDispatchRequestMutationProps = {}

export type UseDispatchRequestMutationResult = {
  acceptDispatchRequest: (dispatchRequestId: string, dispatch: Partial<Dispatch> | undefined, copyDispatchRequestComments: boolean | undefined, onSuccess: (() => void) | undefined) => void
  rejectDispatchRequest: (dispatchRequestId: string, comments: string, onSuccess: (() => void) | undefined) => void
  addDispatchToDispatchRequest: (dispatchRequestId: string, dispatch: Partial<Dispatch> | undefined, copyDispatchRequestComments: boolean | undefined, onSuccess: (() => void) | undefined) => void
  authorizeDispatchRequest: (dispatchRequest: DispatchRequest, comments: string, onSuccess: (() => void) | undefined) => void
  cancelDispatchRequest: (dispatchRequest: DispatchRequest, comments: string, onSuccess: (() => void) | undefined) => void
  reassignDispatchRequest: (dispatchRequestId: string, companyId: string, comments: string, onSuccess: (() => void) | undefined) => void
  updateDispatchRequest: (dispatchRequest: DispatchRequest, onSuccess: (() => void) | undefined) => void
  isAccepting: boolean
  isRejecting: boolean
  isAdding: boolean
  isAuthorizing: boolean
  isCancelling: boolean
  isReassigning: boolean
  isUpdating: boolean
}

export const useDispatchRequestMutation = (props: UseDispatchRequestMutationProps = {}): UseDispatchRequestMutationResult => {
  const {t} = useTranslation(["common"])

  const {userId} = useAuth()

  // state
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_isAuthorizing, _setAuthorizing] = useDebouncedState(false, 500)
  const [_isCancelling, _setCancelling] = useDebouncedState(false, 500)

  // possible mutations
  const [_acceptDispatchRequestMutation, {isLoading: _isAccepting}] = useAcceptDispatchRequestMutation();
  const [_rejectDispatchRequestMutation, {isLoading: _isRejecting}] = useRejectDispatchRequestMutation();
  const [_addDispatchToDispatchRequestMutation, {isLoading: _isAdding}] = useAddDispatchToDispatchRequestMutation();
  const [_updateDispatchRequestMutation, {isLoading: _isUpdating}] = useUpdateDispatchRequestMutation()
  const [_reassignDispatchRequestMutation, {isLoading: _isReassigning}] = useReassignDispatchRequestMutation()

  useEffect(() => {
    _setUpdating(_isAccepting || _isRejecting || _isAdding || _isUpdating || _isReassigning || _isAuthorizing || _isCancelling)
  }, [_isAccepting, _isRejecting, _isUpdating, _isReassigning, _isAuthorizing, _isCancelling, _setUpdating])


  useEffect(() => {
    if (_isAccepting) {
      notifications.show({
        id: "useDispatchRequestMutation_accepting",
        title: t("common.status.accepting", "Accepting") + " " + t("common.entity.dispatchrequest", "Dispatch Request"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchRequestMutation_accepting")
    }
  }, [_isAccepting, t]);

  useEffect(() => {
    if (_isRejecting) {
      notifications.show({
        id: "useDispatchRequestMutation_rejecting",
        title: t("common.status.rejecting", "Rejecting") + " " + t("common.entity.dispatchrequest", "Dispatch Request"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchRequestMutation_rejecting")
    }
  }, [_isRejecting, t]);

  useEffect(() => {
    if (_isAdding) {
      notifications.show({
        id: "useDispatchRequestMutation_adding",
        title: t("common.status.adding", "Adding") + " " + t("common.entity.dispatch", "Dispatch"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchRequestMutation_addding")
    }
  }, [_isAdding, t]);

  useEffect(() => {
    if (_isReassigning) {
      notifications.show({
        id: "useDispatchRequestMutation_reassigning",
        title: t("common.status.reassigning", "Reassigning") + " " + t("common.entity.dispatchrequest", "Dispatch Request"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchRequestMutation_rejecting")
    }
  }, [_isReassigning, t]);

  useEffect(() => {
    if (_isAuthorizing) {
      notifications.show({
        id: "useDispatchRequestMutation_authorizing",
        title: t("common.status.authorizing", "Authorizing") + " " + t("common.entity.dispatchrequest", "Dispatch Request"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchRequestMutation_authorizing")
    }
  }, [_isAuthorizing, t]);

  useEffect(() => {
    if (_isCancelling) {
      notifications.show({
        id: "useDispatchRequestMutation_cancelling",
        title: t("common.status.cancelling", "Cancelling") + " " + t("common.entity.dispatchrequest", "Dispatch Request"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchRequestMutation_cancelling")
    }
  }, [_isCancelling, t]);

  useEffect(() => {
    if (_isUpdating && !_isCancelling && !_isAuthorizing) {
      notifications.show({
        id: "useDispatchRequestMutation_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.dispatchrequest", "Dispatch Request"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchRequestMutation_updating")
    }
  }, [_isUpdating, _isCancelling, _isAuthorizing, t]);

  const handlePromiseResult = useCallback((promise: Promise<ResultTypeFrom<any>>, title: string, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: title,
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      ).finally(() => {
      _setAuthorizing(false)
      _setCancelling(false)
    });
  }, [t, _setAuthorizing, _setCancelling])

  const _acceptDispatchRequest = useCallback((dispatchRequestId: string, dispatch: Partial<Dispatch> | undefined, copyDispatchRequestComments: boolean | undefined, onSuccess: (() => void) | undefined) => {
    const promise = _acceptDispatchRequestMutation({
      dispatchRequestId: dispatchRequestId,
      dispatch: dispatch,
      copyDispatchRequestComments: copyDispatchRequestComments,
    }).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.status.accepted", "Accepted"),
      onSuccess
    )
  }, [_acceptDispatchRequestMutation, handlePromiseResult, t])

  const _rejectDispatchRequest = useCallback((dispatchRequestId: string, comments: string, onSuccess: (() => void) | undefined) => {
    const promise = _rejectDispatchRequestMutation({
      dispatchRequestId: dispatchRequestId,
      comments: comments,
    }).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.status.rejected", "Rejected"),
      onSuccess
    )
  }, [_rejectDispatchRequestMutation, handlePromiseResult, t])

  const _addDispatchToDispatchRequest = useCallback((dispatchRequestId: string, dispatch: Partial<Dispatch> | undefined, copyDispatchRequestComments: boolean | undefined, onSuccess: (() => void) | undefined) => {
    const promise = _addDispatchToDispatchRequestMutation({
      dispatchRequestId: dispatchRequestId,
      dispatch: dispatch,
      copyDispatchRequestComments: copyDispatchRequestComments,
    }).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.dispatch", "Dispatch") + " " + t("common.status.added", "Added"),
      onSuccess
    )
  }, [_addDispatchToDispatchRequestMutation, handlePromiseResult, t])

  const _cancelDispatchRequest = useCallback((dispatchRequest: DispatchRequest, comments: string, onSuccess: (() => void) | undefined) => {
    _setCancelling(true)
    const promise = _updateDispatchRequestMutation({
      ...dispatchRequest,
      status: dispatchRequestStatus.pendingCancel.code,
      canceledById: userId,
      canceledDttm: adjustToTimeZone().getTime(),
      lastModifiedDttm: adjustToTimeZone().getTime(),
      comment: comments,
      authentication: userId,
    }).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.status.cancelled", "Cancelled"),
      onSuccess
    )
  }, [_updateDispatchRequestMutation, _setCancelling, handlePromiseResult, userId, t])

  const _authDispatchRequest = useCallback((dispatchRequest: DispatchRequest, comments: string, onSuccess: (() => void) | undefined) => {
    _setAuthorizing(true)
    const promise = _updateDispatchRequestMutation({
      ...dispatchRequest,
      authorized: true,
      authorizeId: userId,
      authorizeDttm: adjustToTimeZone().getTime(),
      lastModifiedDttm: adjustToTimeZone().getTime(),
      comment: comments,
      authentication: userId,
    }).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.status.authorized", "Authorized"),
      onSuccess
    )
  }, [_updateDispatchRequestMutation, _setAuthorizing, handlePromiseResult, userId, t])

  const _reassignDispatchRequest = useCallback((dispatchRequestId: string, companyId: string, comments: string, onSuccess: (() => void) | undefined) => {
    const promise = _reassignDispatchRequestMutation({
      dispatchRequestId: dispatchRequestId,
      companyId: companyId,
      comments: comments
    }).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.status.reassigned", "Reassigned"),
      onSuccess
    )
  }, [_reassignDispatchRequestMutation, handlePromiseResult, t])

  const _updateDispatchRequest = useCallback((dispatchRequest: DispatchRequest, onSuccess: (() => void) | undefined) => {
    const promise = _updateDispatchRequestMutation({
      ...dispatchRequest
    }).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.dispatchrequest", "Dispatch Request") + " " + t("common.status.updated", "Updated"),
      onSuccess
    )
  }, [_updateDispatchRequestMutation, handlePromiseResult, t])

  return {
    acceptDispatchRequest: _acceptDispatchRequest,
    rejectDispatchRequest: _rejectDispatchRequest,
    addDispatchToDispatchRequest: _addDispatchToDispatchRequest,
    authorizeDispatchRequest: _authDispatchRequest,
    cancelDispatchRequest: _cancelDispatchRequest,
    reassignDispatchRequest: _reassignDispatchRequest,
    updateDispatchRequest: _updateDispatchRequest,
    isAccepting: _isAccepting,
    isRejecting: _isRejecting,
    isAdding: _isAdding,
    isAuthorizing: _isAuthorizing,
    isCancelling: _isCancelling,
    isReassigning: _isReassigning,
    isUpdating: _isUpdating
  }
}
